import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMobileAlt, faSpinner, faLock, faCheck, faCircleInfo } from '@fortawesome/free-solid-svg-icons';

// Reusable IMEI collection modal — used for both the purchase-order flow
// (one group per physical unit, count = order item quantity) and the swap
// flow (always exactly one device, so unitCount=1). IMEI1 is required per
// unit, IMEI2 optional (dual-SIM). Write-once server-side — this modal is
// only ever shown before submission; the value is never shown back to
// whoever submitted it afterward.
//
// Layout notes: the header and the action bar are pinned, and only the unit
// list between them scrolls. With a quantity of three or four phones the
// previous single-scrolling-block version pushed Submit below the fold on a
// phone, which read as a broken modal. Validation is also per-field and live
// rather than one message at the top, so on a multi-unit order the seller can
// see which specific box is wrong without hunting for it.
const IMEISubmissionModal = ({ unitCount = 1, title = 'Enter IMEI', isDarkMode = false, onSubmit, onClose }) => {
  const [entries, setEntries] = useState(
    Array.from({ length: unitCount }, () => ({ imei1: '', imei2: '' }))
  );
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstInputRef = useRef(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Escape closes, matching every other modal in the dashboards.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const isValidIMEI = (v) => /^\d{15}$/.test((v || '').trim());

  const updateEntry = (index, field, value) => {
    setEntries(prev => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  const completedCount = useMemo(
    () => entries.filter(e => isValidIMEI(e.imei1)).length,
    [entries]
  );
  const allValid = useMemo(
    () => entries.every(e => isValidIMEI(e.imei1) && (!e.imei2 || isValidIMEI(e.imei2))),
    [entries]
  );

  const fieldError = (index, field) => {
    if (!touched[`${index}-${field}`]) return '';
    const value = entries[index][field];
    if (field === 'imei1') {
      if (!value) return 'Required';
      if (!isValidIMEI(value)) return `${value.length}/15 digits`;
    } else if (value && !isValidIMEI(value)) {
      return `${value.length}/15 digits`;
    }
    return '';
  };

  const handleSubmit = async () => {
    setError('');
    const allTouched = {};
    entries.forEach((_, i) => { allTouched[`${i}-imei1`] = true; allTouched[`${i}-imei2`] = true; });
    setTouched(allTouched);

    for (let i = 0; i < entries.length; i++) {
      if (!isValidIMEI(entries[i].imei1)) {
        setError(`Unit ${i + 1}: IMEI 1 must be exactly 15 digits`);
        return;
      }
      if (entries[i].imei2 && !isValidIMEI(entries[i].imei2)) {
        setError(`Unit ${i + 1}: IMEI 2 must be exactly 15 digits if provided`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSubmit(entries.map(e => ({ imei1: e.imei1.trim(), imei2: e.imei2.trim() || undefined })));
    } catch (err) {
      setError(err?.message || 'Failed to submit IMEI. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const c = {
    surface: isDarkMode ? '#16161d' : '#ffffff',
    raised: isDarkMode ? 'rgba(255,255,255,0.035)' : '#f8fafc',
    border: isDarkMode ? 'rgba(255,255,255,0.09)' : '#e2e8f0',
    text: isDarkMode ? '#f1f5f9' : '#0f172a',
    muted: isDarkMode ? '#94a3b8' : '#64748b',
    input: isDarkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder: isDarkMode ? 'rgba(255,255,255,0.14)' : '#cbd5e1',
  };

  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '0.7rem 0.85rem',
    borderRadius: '10px',
    border: `1px solid ${hasError ? '#ef4444' : c.inputBorder}`,
    background: c.input,
    color: c.text,
    fontSize: '1rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '0.06em',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  });

  return (
    <div
      onClick={() => !submitting && onClose()}
      style={{
        // Two things put the Submit button off-screen on smaller viewports:
        //
        // 1. `vh` on mobile measures the viewport as if the browser's
        //    address bar were hidden, so a 88vh-tall dialog is genuinely
        //    taller than what you can see — and the pinned action bar is
        //    the very bottom of it. `dvh` is the unit that tracks the
        //    actual visible area.
        // 2. `align-items: center` on a non-scrollable overlay overflows a
        //    too-tall child out BOTH ends, and there's no way to scroll to
        //    reach either. `flex-start` + `margin: auto` on the child keeps
        //    it centred while it fits and lets the overlay scroll once it
        //    doesn't.
        position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.72)', backdropFilter: 'blur(3px)',
        zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '1rem', overflowY: 'auto', overscrollBehavior: 'contain',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: c.surface, color: c.text, borderRadius: '18px',
          // A hard 520px cap read fine on phone and desktop but wasted the
          // extra room on a tablet or small-desktop window — an order with
          // several units just meant a lot more scrolling than the space
          // available justified. Scale with the viewport instead, and widen
          // further once there's more than one unit to lay out (paired with
          // the grid below, so a tablet-width window shows 2 units side by
          // side instead of one long single-file scroll).
          width: '100%',
          maxWidth: unitCount > 1 ? 'min(94vw, 720px)' : 'min(92vw, 480px)',
          // dvh, and minus the overlay's own 1rem top+bottom padding, so the
          // dialog can never be taller than the space it actually has —
          // which is what pushed the pinned action bar out of view.
          maxHeight: 'min(calc(100dvh - 2rem), 760px)',
          margin: 'auto',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${c.border}`,
          boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
        }}
      >
        {/* ── Pinned header ── */}
        <div style={{ padding: '1.25rem 1.4rem 1rem', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
                background: 'rgba(34,197,94,0.14)', color: '#22C55E', fontSize: '1.05rem',
              }}>
                <FontAwesomeIcon icon={faMobileAlt} />
              </span>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {title}
                </h3>
                <div style={{ fontSize: '0.8rem', color: c.muted, marginTop: '0.15rem' }}>
                  {unitCount > 1 ? `${unitCount} devices in this order` : 'One device'}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
              style={{
                background: 'none', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '1.05rem', color: c.muted, padding: '0.35rem', lineHeight: 1, flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Progress — only meaningful with more than one unit to fill in. */}
          {unitCount > 1 && (
            <div style={{ marginTop: '0.95rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: c.muted, marginBottom: '0.35rem' }}>
                <span>{completedCount} of {unitCount} entered</span>
              </div>
              <div style={{ height: '4px', borderRadius: '99px', background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${(completedCount / unitCount) * 100}%`,
                  background: '#22C55E', borderRadius: '99px', transition: 'width 0.25s ease',
                }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Scrolling unit list ── */}
        {/* minHeight:0 is what actually makes the Submit button reachable.
            A flex item defaults to min-height:auto, so this panel refused to
            shrink below its own content — with a few unit cards in it the
            column grew past the dialog's max height, `overflowY: auto` never
            engaged (nothing was constraining it), and the pinned footer
            holding Submit was pushed beyond the edge and clipped away by the
            dialog's overflow:hidden. Same trap as min-width:0 on a flex row. */}
        <div style={{ padding: '1.1rem 1.4rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div style={{
            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
            padding: '0.75rem 0.85rem', marginBottom: '1.1rem', borderRadius: '10px',
            background: isDarkMode ? 'rgba(245,158,11,0.09)' : 'rgba(245,158,11,0.08)',
            border: `1px solid ${isDarkMode ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.3)'}`,
          }}>
            <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#f59e0b', marginTop: '0.15rem', flexShrink: 0 }} />
            <div style={{ fontSize: '0.82rem', lineHeight: 1.5, color: c.text }}>
              Submitted <strong>once</strong> and cannot be viewed or edited afterward — check each number before submitting.
              Dial <strong style={{ fontFamily: 'ui-monospace, monospace' }}>*#06#</strong> on the device to display its IMEI.
            </div>
          </div>

          {/* auto-fit + minmax is inherently responsive with no breakpoints
              needed: one column on a phone, two (or more) once the modal is
              wide enough — which it now is on tablet / small-desktop, so an
              order with several units doesn't turn into one long scroll. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: unitCount > 1 ? 'repeat(auto-fit, minmax(240px, 1fr))' : '1fr',
            gap: '0.9rem',
          }}>
          {entries.map((entry, i) => {
            const err1 = fieldError(i, 'imei1');
            const err2 = fieldError(i, 'imei2');
            const unitDone = isValidIMEI(entry.imei1);
            return (
              <div
                key={i}
                style={{
                  padding: '1rem', borderRadius: '13px',
                  background: c.raised,
                  border: `1px solid ${unitDone ? 'rgba(34,197,94,0.35)' : c.border}`,
                  transition: 'border-color 0.2s ease',
                }}
              >
                {unitCount > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Device {i + 1} of {unitCount}</span>
                    {unitDone && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#22C55E', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FontAwesomeIcon icon={faCheck} /> Ready
                      </span>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: '0.8rem' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.35rem', color: c.muted }}>
                    <span>IMEI 1 <span style={{ color: '#ef4444' }}>*</span></span>
                    {err1 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{err1}</span> : null}
                  </label>
                  <input
                    ref={i === 0 ? firstInputRef : null}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={15}
                    value={entry.imei1}
                    onChange={(e) => updateEntry(i, 'imei1', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => setTouched(t => ({ ...t, [`${i}-imei1`]: true }))}
                    placeholder="15-digit IMEI"
                    style={inputStyle(!!err1)}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.35rem', color: c.muted }}>
                    <span>IMEI 2 <span style={{ fontWeight: 500 }}>(optional — dual-SIM)</span></span>
                    {err2 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{err2}</span> : null}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={15}
                    value={entry.imei2}
                    onChange={(e) => updateEntry(i, 'imei2', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => setTouched(t => ({ ...t, [`${i}-imei2`]: true }))}
                    placeholder="Leave blank if single-SIM"
                    style={inputStyle(!!err2)}
                  />
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {/* ── Pinned action bar ── */}
        <div style={{ padding: '1rem 1.4rem 1.2rem', borderTop: `1px solid ${c.border}`, flexShrink: 0, background: c.surface }}>
          {error && (
            <div style={{
              color: '#ef4444', fontSize: '0.84rem', marginBottom: '0.8rem',
              padding: '0.6rem 0.75rem', borderRadius: '9px',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.28)',
            }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.75rem', color: c.muted, marginBottom: '0.85rem' }}>
            <FontAwesomeIcon icon={faLock} />
            <span>Visible only to AutoboyExpress admin — never shown to the buyer.</span>
          </div>
          <div style={{ display: 'flex', gap: '0.7rem' }}>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: '0 0 auto', padding: '0.72rem 1.3rem', borderRadius: '11px',
                border: `1px solid ${c.inputBorder}`, background: 'transparent', color: c.text,
                fontWeight: 600, fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !allValid}
              style={{
                flex: 1, padding: '0.72rem 1.3rem', borderRadius: '11px', border: 'none',
                background: allValid && !submitting ? 'linear-gradient(135deg,#22C55E,#16A34A)' : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                color: allValid && !submitting ? '#fff' : c.muted,
                fontWeight: 700, fontSize: '0.9rem',
                cursor: submitting || !allValid ? 'not-allowed' : 'pointer',
                boxShadow: allValid && !submitting ? '0 6px 18px rgba(34,197,94,0.32)' : 'none',
                transition: 'background 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              {submitting
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting…</>
                : unitCount > 1 ? `Submit ${unitCount} IMEIs` : 'Submit IMEI'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IMEISubmissionModal;
